/**
 * Microphone analysis: RMS volume + autocorrelation pitch, polled per frame.
 * Only derived scalars ever leave this module — raw audio never goes anywhere.
 */
import type { AudioSnapshot } from '../core/module-api';

export class AudioEngine {
  enabled = false;
  private enabling = false;
  private manual = false;
  private ctx?: AudioContext;
  private analyser?: AnalyserNode;
  private stream?: MediaStream;
  private timeBuf?: Float32Array<ArrayBuffer>;
  private vol = 0;
  private pitch: number | null = null;

  /** Request mic access (must be called from a user gesture). Safe to call repeatedly. */
  async enable(): Promise<boolean> {
    if (this.enabled || this.enabling) return this.enabled;
    this.enabling = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const ctx = new AudioContext();
      await ctx.resume();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.1;
      src.connect(analyser);
      this.stream = stream;
      this.ctx = ctx;
      this.analyser = analyser;
      this.timeBuf = new Float32Array(analyser.fftSize);
      this.enabled = true;
    } catch {
      this.enabled = false;
    } finally {
      this.enabling = false;
    }
    return this.enabled;
  }

  /** Sample the mic. Called once per frame while enabled. */
  update(): void {
    if (this.manual || !this.enabled || !this.analyser || !this.timeBuf || !this.ctx) return;
    this.analyser.getFloatTimeDomainData(this.timeBuf);
    let sum = 0;
    for (let i = 0; i < this.timeBuf.length; i++) {
      const v = this.timeBuf[i];
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.timeBuf.length);
    const vol = Math.max(0, Math.min(1, rms * 4));
    this.vol = this.vol * 0.6 + vol * 0.4;
    this.pitch = detectPitch(this.timeBuf, this.ctx.sampleRate);
  }

  snapshot(): AudioSnapshot {
    return { volume: this.vol, pitch: this.pitch };
  }

  /** Test hook: drive volume/pitch directly without a real microphone. */
  debugSet(volume: number, pitch: number | null): void {
    this.enabled = true;
    this.manual = true;
    this.vol = volume;
    this.pitch = pitch;
  }
}

/** Monophonic pitch via normalised autocorrelation with parabolic refinement. */
function detectPitch(buf: Float32Array, sampleRate: number): number | null {
  const size = buf.length;
  let rms = 0;
  for (let i = 0; i < size; i++) rms += buf[i] * buf[i];
  rms = Math.sqrt(rms / size);
  if (rms < 0.01) return null; // too quiet to be confident

  let r1 = 0;
  let r2 = size - 1;
  const thres = 0.2;
  for (let i = 0; i < size / 2; i++) {
    if (Math.abs(buf[i]) < thres) {
      r1 = i;
      break;
    }
  }
  for (let i = 1; i < size / 2; i++) {
    if (Math.abs(buf[size - i]) < thres) {
      r2 = size - i;
      break;
    }
  }
  const b = buf.subarray(r1, r2);
  const n = b.length;
  if (n < 2) return null;

  const c = new Float32Array(n);
  for (let lag = 0; lag < n; lag++) {
    let s = 0;
    for (let i = 0; i < n - lag; i++) s += b[i] * b[i + lag];
    c[lag] = s;
  }

  let d = 0;
  while (d < n - 1 && c[d] > c[d + 1]) d++;
  let maxVal = -1;
  let maxPos = -1;
  for (let i = d; i < n; i++) {
    if (c[i] > maxVal) {
      maxVal = c[i];
      maxPos = i;
    }
  }
  if (maxPos <= 0) return null;

  let t0 = maxPos;
  const x1 = c[t0 - 1] ?? 0;
  const x2 = c[t0];
  const x3 = c[t0 + 1] ?? 0;
  const a = (x1 + x3 - 2 * x2) / 2;
  const bb = (x3 - x1) / 2;
  if (a !== 0) t0 = t0 - bb / (2 * a);

  const freq = sampleRate / t0;
  if (freq < 50 || freq > 2000) return null;
  return freq;
}
